import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Loader2 } from 'lucide-react';
import api from '../api/client';
import { toast } from '../hooks/use-toast';

import CompanyLookupBar from './company/CompanyLookupBar';
import CompanyIdentitySection from './company/CompanyIdentitySection';
import CompanyTaxSection from './company/CompanyTaxSection';
import CompanyContactSection from './company/CompanyContactSection';
import CompanySirenePicker from './company/CompanySirenePicker';

/**
 * Company form dialog (Vague 97.4 — orchestrator only).
 * Form state lives here; section rendering is delegated to the sub-components
 * under `./company/`. Lookup + VIES side-effects also live here so the
 * sections stay stateless.
 */
const EMPTY_VALUES = {
  name: '', siret: '', siren: '', legal_form: '', naf_code: '', sector: '',
  address: '', postal_code: '', city: '', pays: '',
  // Vides : c'est l'adresse du siège qui figure sur les documents.
  facturation_adresse: '', facturation_code_postal: '',
  facturation_ville: '', facturation_pays: '',
  contact_name: '', contact_email: '', contact_phone: '',
  // Vide : c'est le contact du plan de formation qui reçoit les factures.
  facturation_nom: '', facturation_email: '',
  website: '', employees_count: 0, notes: '',
  tva_intra: '', liste_idcc: [],
  leader_name: '', leader_role: '', capital_social: 0,
  opco_id: null,
};

export default function CompanyFormDialog({ open, onOpenChange, initialValues, onSubmit }) {
  const isEdit = !!initialValues;
  const [values, setValues] = useState(EMPTY_VALUES);
  const [lookupQuery, setLookupQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [looking, setLooking] = useState(false);
  const [lookupInfo, setLookupInfo] = useState(null);
  const [error, setError] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerResults, setPickerResults] = useState([]);
  const [pickerTotal, setPickerTotal] = useState(0);
  const [viesState, setViesState] = useState({ status: 'idle' });

  useEffect(() => {
    if (open) {
      // Re-init from initialValues every time the dialog opens (prevents leak from previous edit).
      setValues({ ...EMPTY_VALUES, ...(initialValues || {}) });
      setLookupQuery(initialValues?.siret || initialValues?.siren || '');
    } else {
      setValues(EMPTY_VALUES);
      setLookupQuery('');
    }
    setError(null);
    setLookupInfo(null);
    setViesState({ status: 'idle' });
    setPickerOpen(false);
    setPickerResults([]);
    setPickerTotal(0);
  }, [open, initialValues]);

  const set = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));

  const applyLookupResult = (data) => {
    setValues((v) => ({
      ...v,
      name: data.name || v.name,
      siret: data.siret || v.siret,
      siren: data.siren || v.siren,
      legal_form: data.legal_form || v.legal_form,
      naf_code: data.naf_code || v.naf_code,
      sector: data.sector || v.sector,
      address: data.address || v.address,
      postal_code: data.postal_code || v.postal_code,
      city: data.city || v.city,
      employees_count: data.employees_count || v.employees_count,
      tva_intra: data.tva_intra || v.tva_intra,
      liste_idcc: (data.liste_idcc && data.liste_idcc.length) ? data.liste_idcc : v.liste_idcc,
      leader_name: data.leader_name || v.leader_name,
      leader_role: data.leader_role || v.leader_role,
      capital_social: data.capital_social || v.capital_social,
    }));
    setLookupInfo({ createdDate: data.created_date, isActive: data.is_active });
    setViesState({ status: 'idle' });
  };

  const runLookup = async () => {
    const q = (lookupQuery || values.siret || values.name || '').trim();
    if (!q) { toast({ title: 'Saisissez un SIRET, SIREN ou raison sociale' }); return; }
    const digits = q.replace(/\s/g, '');
    const isIdentifier = /^\d{9}$|^\d{14}$/.test(digits);
    setLooking(true);
    try {
      if (isIdentifier) {
        const { data } = await api.get('/companies/sirene-lookup', { params: { q: digits } });
        applyLookupResult(data);
        toast({ title: 'Informations récupérées', description: data.name });
      } else {
        const { data } = await api.get('/companies/sirene-search', { params: { q, per_page: 8 } });
        const items = data?.items || [];
        if (items.length === 0) {
          toast({ title: 'Recherche', description: 'Aucune entreprise trouvée pour « ' + q + ' »' });
        } else if (items.length === 1) {
          applyLookupResult(items[0]);
          toast({ title: 'Informations récupérées', description: items[0].name });
        } else {
          setPickerResults(items);
          setPickerTotal(data?.total || items.length);
          setPickerOpen(true);
        }
      }
    } catch (e) {
      const status = e?.response?.status;
      let msg;
      if (status === 429 || status === 502) msg = 'API publique saturée, réessayez dans un instant';
      else if (status === 404) msg = 'Aucune entreprise trouvée pour cette requête';
      else if (status === 400) msg = e?.response?.data?.detail || 'Requête invalide';
      else msg = e?.response?.data?.detail || 'Erreur lors de la recherche';
      toast({ title: 'Recherche', description: msg });
    }
    setLooking(false);
  };

  const checkVies = async () => {
    const vat = (values.tva_intra || '').trim().toUpperCase();
    if (!vat) { toast({ title: 'Saisissez un numéro de TVA' }); return; }
    setViesState({ status: 'loading' });
    try {
      const { data } = await api.get('/companies/vies-check', { params: { vat } });
      if (data.valid) {
        setViesState({ status: 'valid', name: data.name, address: data.address });
        toast({ title: 'TVA validée par VIES', description: data.name || 'Numéro actif' });
      } else {
        setViesState({ status: 'invalid' });
        toast({ title: 'TVA non validée', description: 'Le service VIES ne reconnaît pas ce numéro.' });
      }
    } catch (e) {
      setViesState({ status: 'error' });
      toast({ title: 'VIES', description: e?.response?.data?.detail || 'Service indisponible' });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!values.name?.trim()) { setError('La raison sociale est requise.'); return; }
    setLoading(true);
    setError(null);
    try {
      await onSubmit(values);
      onOpenChange(false);
    } catch (e2) {
      setError(e2?.response?.data?.detail || 'Erreur lors de la sauvegarde.');
    }
    setLoading(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[820px] max-h-[92vh] overflow-y-auto" data-testid="company-form-dialog">
          <DialogHeader>
            <DialogTitle>{isEdit ? "Modifier l'entreprise" : 'Nouvelle entreprise'}</DialogTitle>
            <DialogDescription>
              Renseignez un <b>SIRET/SIREN</b> ou une <b>raison sociale</b> puis cliquez sur <b>Rechercher</b> pour importer automatiquement les informations légales depuis <a className="text-[#4F46E5] hover:underline" target="_blank" rel="noreferrer" href="https://recherche-entreprises.api.gouv.fr">recherche-entreprises.api.gouv.fr</a>.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-5">
            <CompanyLookupBar
              lookupQuery={lookupQuery}
              setLookupQuery={setLookupQuery}
              looking={looking}
              runLookup={runLookup}
              lookupInfo={lookupInfo}
              values={values}
            />

            <CompanyIdentitySection values={values} set={set} />

            <CompanyTaxSection
              values={values}
              setValues={setValues}
              viesState={viesState}
              setViesState={setViesState}
              checkVies={checkVies}
            />

            <CompanyContactSection values={values} set={set} />

            {error && (
              <div className="text-[12.5px] text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <DialogFooter>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="h-10 px-4 rounded-xl bg-[#F6F7FB] text-[#4A4B63] text-[13px] font-semibold"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                data-testid="company-form-submit"
                className="h-10 px-5 rounded-xl brand-gradient text-white text-[13px] font-semibold inline-flex items-center gap-1.5 shadow-sm disabled:opacity-60"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {isEdit ? 'Enregistrer' : 'Créer'}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <CompanySirenePicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        results={pickerResults}
        total={pickerTotal}
        onPick={applyLookupResult}
      />
    </>
  );
}
